/**
 * HustleMeetLandingPage.jsx — /hustlemeet
 *
 * Umbrella HustleMeet sales page. Two sub-products live here:
 *   · Circles    → /circles    (host-led recurring dinners)
 *   · Matchmaker → /matchmaker (AI-matched shared tables)
 *
 * Fully public — no login required. NiceToMeet + Meetup positioning.
 * Every string is i18n-wrapped via `hm(t, "landing.<key>")`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Utensils, Users, Sparkles, MapPin, ArrowRight, Heart, Shield,
  Star, CheckCircle2, XCircle, MessageSquare, Calendar,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import HustleMeetLeaderboard from "@/components/HustleMeetLeaderboard";
import HustlePointsPromoBanner from "@/components/HustlePointsPromoBanner";
import { Button } from "@/components/ui/button";
import { hm } from "@/lib/hmCopy";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const LIVE_CITIES = [
  "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide",
  "Gold Coast", "Canberra", "Hobart", "Newcastle", "Wollongong",
  "Sunshine Coast", "Cairns", "Auckland", "Wellington", "Christchurch",
];

export default function HustleMeetLandingPage() {
  const { t, i18n } = useTranslation();
  const [circleCount, setCircleCount] = useState(null);
  const [wallPhotos, setWallPhotos] = useState([]);

  const HM_NAV = [
    { to: "/meet",             label: hm(t, "nav.hustlemeet") },
    { to: "/circles",     label: hm(t, "nav.circles") },
    { to: "/matchmaker",  label: hm(t, "nav.matchmaker") },
    { to: "/verified-members",       label: hm(t, "nav.verified") },
  ];

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = hm(t, "landing.page_title");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hustlemeet-circles/catalog`);
        setCircleCount((data?.circles || []).length);
      } catch { setCircleCount(0); }
      try {
        const { data } = await axios.get(
          `${API}/hustlemeet-circles/recent-photos?limit=24`);
        setWallPhotos((data?.photos || []).slice(0, 24));
      } catch { setWallPhotos([]); }
    })();
  }, [t, i18n.language]);

  const heroTitle = hm(t, "landing.hero_title");
  const heroAccent = hm(t, "landing.hero_title_accent");
  const heroTitleParts = heroTitle.split(heroAccent);

  const citiesSub =
    circleCount === null
      ? hm(t, "landing.cities_sub_some")
      : circleCount === 0
        ? hm(t, "landing.cities_sub_zero")
        : circleCount === 1
          ? hm(t, "landing.cities_sub_one", { count: circleCount })
          : hm(t, "landing.cities_sub_many", { count: circleCount });

  return (
    <div
      data-testid="hustlemeet-landing-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: hm(t, "nav.hustlemeet"), to: "/meet" }}
        nav={HM_NAV}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section
        data-testid="hustlemeet-hero"
        className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800"
        style={{ background: "linear-gradient(135deg,#0a0908 0%,#1c1917 55%,#7c2d12 120%)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white">
          <p className="text-amber-400 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "landing.kicker")}
          </p>
          <h1 className="mt-3 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl">
            {heroTitleParts.length === 2 ? (
              <>{heroTitleParts[0]}<span className="text-amber-400">{heroAccent}</span>{heroTitleParts[1]}</>
            ) : heroTitle}
          </h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl">
            {hm(t, "landing.hero_sub")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
              data-testid="cta-hero-circles"
            >
              <Link to="/circles">
                {hm(t, "landing.cta_explore_circles")} <ArrowRight size={14} className="ml-1" aria-hidden="true"/>
              </Link>
            </Button>
            <Button asChild variant="outline"
              className="rounded-full border-amber-400 text-amber-300 hover:bg-amber-500/10 font-black h-12 px-6"
              data-testid="cta-hero-matchmaker"
            >
              <Link to="/matchmaker">
                {hm(t, "landing.cta_try_matchmaker")} <ArrowRight size={14} className="ml-1" aria-hidden="true"/>
              </Link>
            </Button>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl text-white/70 text-xs">
            {[
              [Shield,        hm(t, "landing.trust_verified")],
              [Utensils,      hm(t, "landing.trust_venues")],
              [Heart,         hm(t, "landing.trust_friendship")],
              [MessageSquare, hm(t, "landing.trust_moderation")],
            ].map(([I, lbl], i) => (
              <div key={i} className="inline-flex items-center gap-2">
                <I size={14} className="text-amber-400"/> {lbl}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">

        {/* Hustle Points promo — loyalty callout on every landing */}
        <HustlePointsPromoBanner variant="dark" />

        {/* Host leaderboard — Sprint 2 (g) */}
        <HustleMeetLeaderboard />

        {/* Two Ways In */}
        <section data-testid="sub-brands" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/circles" data-testid="subbrand-circles"
            className="group rounded-3xl border-2 border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 hover:border-amber-400 transition"
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Users size={26} aria-label="HustleMeet Circles" title="HustleMeet Circles"/>
            </div>
            <p className="mt-5 text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              {hm(t, "landing.subbrand_circles_kicker")}
            </p>
            <h2 className="mt-1 text-2xl md:text-3xl font-black group-hover:text-amber-500 transition">
              {hm(t, "landing.subbrand_circles_title")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 leading-relaxed">
              {hm(t, "landing.subbrand_circles_body")}
            </p>
            <ul className="mt-5 space-y-1.5 text-sm text-stone-700 dark:text-stone-300">
              {["subbrand_circles_b1", "subbrand_circles_b2", "subbrand_circles_b3"].map((k) => (
                <li key={k} className="inline-flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-amber-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                  {hm(t, `landing.${k}`)}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm font-black text-amber-500 inline-flex items-center gap-1">
              {hm(t, "landing.subbrand_circles_cta")} <ArrowRight size={14} aria-hidden="true"/>
            </p>
          </Link>

          <Link to="/matchmaker" data-testid="subbrand-matchmaker"
            className="group rounded-3xl border-2 border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-8 hover:border-amber-500 transition"
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 text-amber-500 flex items-center justify-center">
              <Sparkles size={26} aria-label="HustleMeet Matchmaker" title="HustleMeet Matchmaker"/>
            </div>
            <p className="mt-5 text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              {hm(t, "landing.subbrand_matchmaker_kicker")}
            </p>
            <h2 className="mt-1 text-2xl md:text-3xl font-black group-hover:text-amber-500 transition">
              {hm(t, "landing.subbrand_matchmaker_title")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 leading-relaxed">
              {hm(t, "landing.subbrand_matchmaker_body")}
            </p>
            <ul className="mt-5 space-y-1.5 text-sm text-stone-700 dark:text-stone-300">
              {["subbrand_matchmaker_b1", "subbrand_matchmaker_b2", "subbrand_matchmaker_b3"].map((k) => (
                <li key={k} className="inline-flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-amber-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                  {hm(t, `landing.${k}`)}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm font-black text-amber-500 inline-flex items-center gap-1">
              {hm(t, "landing.subbrand_matchmaker_cta")} <ArrowRight size={14} aria-hidden="true"/>
            </p>
          </Link>
        </section>

        {/* How it works */}
        <section id="how" data-testid="how-it-works">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "landing.how_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "landing.how_title")}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} data-testid={`step-${n}`}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5"
              >
                <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                  {hm(t, "landing.how_step_num", { defaultValue: `Step ${n}` }).replace(/\{n\}/, n)}
                </p>
                <p className="mt-2 font-black">{hm(t, `landing.how_step${n}_ttl`)}</p>
                <p className="mt-1 text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `landing.how_step${n}_sub`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section data-testid="stats-strip"
          className="rounded-3xl bg-gradient-to-br from-amber-400/10 to-orange-500/10 border border-amber-300 dark:border-amber-800 p-8 md:p-12"
        >
          <p className="text-center text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "landing.stats_kicker")}
          </p>
          <h2 className="mt-2 text-center text-2xl md:text-3xl font-black">
            {hm(t, "landing.stats_title")}
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[["92%", "stats_92_sub"], ["68%", "stats_68_sub"], ["85%", "stats_85_sub"]].map(([n, k]) => (
              <div key={n} data-testid={`stat-${n}`} className="text-center">
                <p className="text-4xl md:text-6xl font-black text-amber-500">{n}</p>
                <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 max-w-xs mx-auto">
                  {hm(t, `landing.${k}`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Why we work */}
        <section data-testid="why-work">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "landing.why_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "landing.why_title")}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              [Sparkles, 1], [MapPin, 2], [Calendar, 3],
              [Shield, 4],   [Heart, 5],  [Users, 6],
            ].map(([Ico, n]) => (
              <div key={n} className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 font-black">{hm(t, `landing.why_${n}_ttl`)}</p>
                <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `landing.why_${n}_body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Cities */}
        <section data-testid="cities-strip"
          className="rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 md:p-12"
        >
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "landing.cities_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "landing.cities_title")}</h2>
          <p className="mt-3 text-sm text-stone-700 dark:text-stone-300 max-w-2xl">{citiesSub}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {LIVE_CITIES.map((city) => (
              <span key={city}
                data-testid={`city-${city.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"
              >
                <MapPin size={11} className="text-amber-500" aria-label="City" title="City"/> {city}
              </span>
            ))}
          </div>
        </section>

        {/* For me / Not for me */}
        <section data-testid="not-for-you" className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              {hm(t, "landing.forme_kicker")}
            </p>
            <h3 className="mt-1 text-xl md:text-2xl font-black">{hm(t, "landing.forme_title")}</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {[1, 2, 3, 4].map((n) => (
                <li key={n} className="inline-flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                  <span>{hm(t, `landing.forme_${n}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              {hm(t, "landing.notme_kicker")}
            </p>
            <h3 className="mt-1 text-xl md:text-2xl font-black">{hm(t, "landing.notme_title")}</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {[1, 2, 3, 4].map((n) => (
                <li key={n} className="inline-flex items-start gap-2">
                  <XCircle size={16} className="text-stone-600 dark:text-stone-300 shrink-0 mt-0.5"/>
                  <span>{hm(t, `landing.notme_${n}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Safety */}
        <section data-testid="safety-commitments"
          className="rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 md:p-12"
        >
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "landing.safety_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "landing.safety_title")}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[[Shield, 1], [Users, 2], [Star, 3], [MessageSquare, 4]].map(([Ico, n]) => (
              <div key={n}>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 font-black">{hm(t, `landing.safety_${n}_ttl`)}</p>
                <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `landing.safety_${n}_body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Live Photo Wall — most recent Circle photos across the ecosystem */}
        {wallPhotos.length > 0 && (
          <section data-testid="photo-wall" className="pt-2">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-2xl md:text-3xl font-black">
                Live from the Circles
              </h2>
              <Link to="/circles"
                    data-testid="photo-wall-view-all"
                    className="text-xs font-black uppercase tracking-[0.18em] text-amber-500 hover:underline">
                Browse Circles →
              </Link>
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300 max-w-2xl mb-6">
              A rolling wall of the most recent moments members have shared from HustleMeet dinners around the country.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="photo-wall-grid">
              {wallPhotos.map((p) => (
                <Link key={p.id}
                      to={`/circles/${p.circle_slug || p.circle_id}`}
                      data-testid={`photo-wall-item-${p.id}`}
                      title={`${p.circle_name} — ${p.uploader_name}`}
                      className="relative aspect-square overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 group">
                  <img src={p.thumb_url || p.url}
                       alt={p.caption || `${p.circle_name} moment`}
                       loading="lazy"
                       className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 to-transparent p-2 flex items-end">
                    <span className="text-[10px] font-bold text-white line-clamp-2 leading-tight">
                      {p.circle_name}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section data-testid="bottom-cta" className="text-center">
          <Sparkles size={28} className="mx-auto text-amber-500" aria-label="HustleMeet Matchmaker" title="HustleMeet Matchmaker"/>
          <h2 className="mt-4 text-2xl md:text-4xl font-black max-w-2xl mx-auto">
            {hm(t, "landing.bottom_title")}
          </h2>
          <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 max-w-2xl mx-auto">
            {hm(t, "landing.bottom_sub")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
              data-testid="cta-bottom-circles"
            >
              <Link to="/circles">
                {hm(t, "landing.bottom_cta_circles")} <ArrowRight size={14} className="ml-1" aria-hidden="true"/>
              </Link>
            </Button>
            <Button asChild variant="outline"
              className="rounded-full border-amber-400 text-amber-500 hover:bg-amber-500/10 font-black h-12 px-6"
              data-testid="cta-bottom-matchmaker"
            >
              <Link to="/matchmaker">
                {hm(t, "landing.bottom_cta_mm")} <ArrowRight size={14} className="ml-1" aria-hidden="true"/>
              </Link>
            </Button>
          </div>
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
